import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_MAP } from '../data/routes';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const allowedLocales = ['tr', 'en', 'az'];
const resolveLocale = (locale) => (allowedLocales.includes(locale) ? locale : 'tr');

const normalizePath = (pathname) => {
  if (!pathname) return '/';
  const noQuery = pathname.split('?')[0].split('#')[0] || '/';
  if (noQuery.length > 1 && noQuery.endsWith('/')) return noQuery.slice(0, -1);
  return noQuery;
};

const localeRoutes = Object.entries(ROUTE_MAP).flatMap(([locale, routes]) =>
  Object.values(routes)
    .filter((route) => route && route !== '/')
    .map((route) => ({ locale, route }))
);

// Longer routes first to avoid partial collisions.
localeRoutes.sort((a, b) => b.route.length - a.route.length);

const detectLocaleFromPath = (pathname, fallbackLocale = 'tr') => {
  const normalizedPath = normalizePath(pathname);
  if (normalizedPath === '/') return null;

  const matches = localeRoutes
    .filter(({ route }) => normalizedPath === route || normalizedPath.startsWith(`${route}/`))
    .map(({ locale }) => locale);

  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  if (matches.includes(fallbackLocale)) return fallbackLocale;
  if (matches.includes('tr')) return 'tr';
  if (matches.includes('en')) return 'en';
  if (matches.includes('az')) return 'az';
  return null;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const [locale, setLocale] = useState(() => {
    const saved = resolveLocale(localStorage.getItem('demart-language') || 'tr');
    const detected = detectLocaleFromPath(window.location.pathname, saved);
    return detected || saved;
  });

  const resolvedLocale = resolveLocale(locale);

  useEffect(() => {
    const detected = detectLocaleFromPath(location.pathname, resolvedLocale);
    if (detected && detected !== resolvedLocale) {
      setLocale(detected);
    }
  }, [location.pathname, resolvedLocale]);

  useEffect(() => {
    localStorage.setItem('demart-language', resolvedLocale);
    document.documentElement.lang = resolvedLocale;
  }, [resolvedLocale]);

  // TR / EN / AZ helper - NO FALLBACK
  const t = (trText, enText, azText) => {
    if (resolvedLocale === 'tr') return trText;
    if (resolvedLocale === 'en') return enText;
    if (resolvedLocale === 'az') return azText;
    return trText;
  };

  return (
    <LanguageContext.Provider
      value={{
        locale: resolvedLocale,
        language: resolvedLocale,
        setLocale,
        setLanguage: setLocale,
        t,
        detectLocaleFromPath: (pathname) => detectLocaleFromPath(pathname, resolvedLocale),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
